import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '../ui';
import { isChunkLoadError } from '../../utils/lazyWithRetry';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  message: string;
  isChunkError: boolean;
}

/**
 * Catches render errors in the admin tree so a single page failure
 * does not blank the entire shell.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '', isChunkError: false };

  static getDerivedStateFromError(error: Error): State {
    const chunk = isChunkLoadError(error);
    return {
      hasError: true,
      isChunkError: chunk,
      message: chunk
        ? 'This page was updated. Reload to load the latest version.'
        : error?.message || 'An unexpected error occurred.',
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Admin ErrorBoundary:', error, info.componentStack);
    if (isChunkLoadError(error)) {
      try {
        const key = 'bh_admin_chunk_boundary_reload';
        if (sessionStorage.getItem(key) !== '1') {
          sessionStorage.setItem(key, '1');
          window.location.reload();
        }
      } catch {
        /* ignore */
      }
    }
  }

  private handleReload = () => {
    try {
      sessionStorage.removeItem('bh_admin_chunk_reload');
      sessionStorage.removeItem('bh_admin_chunk_boundary_reload');
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/admin';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center text-center p-4"
        style={{ minHeight: '50vh' }}
        role="alert"
      >
        <h1 className="h4 mb-2">
          {this.state.isChunkError
            ? 'Update available'
            : this.props.fallbackTitle || 'Something went wrong'}
        </h1>
        <p className="text-muted mb-4" style={{ maxWidth: 28 * 16 }}>
          {this.state.message}
        </p>
        <div className="d-flex flex-wrap gap-2 justify-content-center">
          <Button type="button" onClick={this.handleReload}>
            Reload page
          </Button>
          {!this.state.isChunkError ? (
            <Button type="button" variant="secondary" onClick={this.handleGoHome}>
              Back to dashboard
            </Button>
          ) : null}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
